import { supabase } from './supabaseClient';

function sortByOrder(a, b) {
  return a.sort_order - b.sort_order;
}

export function isProductVisibleOnStorefront(product) {
  if (!product) return false;
  if (product.hide_product) return false;

  const status = product.status || 'published';
  if (status !== 'published') return false;

  return true;
}

export function normalizeProductImages(product) {
  const images = [...(product.product_images || [])].sort(sortByOrder);

  if (images.length === 0 && product.image_url) {
    return [{ id: 'legacy', image_url: product.image_url, sort_order: 0 }];
  }

  return images;
}

export function normalizeProductSizes(product) {
  const allSizes = [...(product.product_sizes || [])].sort(sortByOrder);
  const sellableSizes = allSizes.filter(
    size => size.enabled !== false && size.stock_quantity > 0
  );

  return {
    sizes: allSizes,
    hasSizeVariants: allSizes.length > 0,
    isOutOfStock: allSizes.length > 0 && sellableSizes.length === 0,
  };
}

export function isSizeAvailable(size) {
  return size.enabled !== false && size.stock_quantity > 0;
}

const PRODUCT_LIST_COLUMNS = `
  id,
  name,
  brand,
  brand_slug,
  team,
  league,
  sport,
  category,
  apparel_type,
  gender,
  tags,
  description,
  price,
  sale_price,
  color,
  image_url,
  created_at,
  status,
  hide_product,
  allow_search,
  show_in_category,
  show_on_homepage,
  featured,
  new_arrivals,
  trending,
  recommended,
  product_images (
    id,
    image_url,
    sort_order,
    is_thumbnail
  ),
  product_sizes (
    id,
    size_label,
    stock_quantity,
    sort_order,
    enabled
  )
`;

export const BESTSELLER_LIMIT = 8;
export const SEARCH_RESULT_LIMIT = 8;

function filterStorefrontProducts(products, { forSearch = false, forCategory = false } = {}) {
  return (products || []).filter(product => {
    if (!isProductVisibleOnStorefront(product)) return false;
    if (forSearch && product.allow_search === false) return false;
    if (forCategory && product.show_in_category === false) return false;
    return true;
  });
}

export async function fetchProducts() {
  const { data, error } = await supabase
    .from('products')
    .select(PRODUCT_LIST_COLUMNS)
    .order('name');

  if (!error) {
    return filterStorefrontProducts(data);
  }

  // Fallback for databases that have not run newer SQL migrations yet
  const fallback = await supabase
    .from('products')
    .select(`
      id, name, brand, team, league, sport, category, price, sale_price, color, image_url, created_at,
      status, hide_product, allow_search, show_in_category, show_on_homepage,
      featured, new_arrivals, trending, recommended, tags, description,
      product_images (id, image_url, sort_order, is_thumbnail),
      product_sizes (id, size_label, stock_quantity, sort_order, enabled)
    `)
    .order('name');

  if (!fallback.error) {
    return filterStorefrontProducts(fallback.data);
  }

  const legacy = await supabase
    .from('products')
    .select(`
      id, name, brand, category, price, sale_price, color, image_url, created_at,
      status, hide_product, allow_search, show_in_category, show_on_homepage,
      featured, new_arrivals, trending, recommended,
      product_images (id, image_url, sort_order, is_thumbnail)
    `)
    .order('name');

  if (!legacy.error) {
    return filterStorefrontProducts(legacy.data);
  }

  const basic = await supabase
    .from('products')
    .select('id, name, category, price, color, image_url, created_at')
    .order('name');

  if (basic.error) throw error;
  return basic.data || [];
}

export async function fetchBestsellers(limit = BESTSELLER_LIMIT) {
  const { data, error } = await supabase.rpc('get_bestsellers', {
    result_limit: limit,
  });

  if (error) {
    throw error;
  }

  return filterStorefrontProducts(data || []).slice(0, limit);
}

export function sortProductsAsBestsellers(products, limit = BESTSELLER_LIMIT) {
  return filterStorefrontProducts(products)
    .sort((a, b) => {
      const aPurchases = a.purchase_count || 0;
      const bPurchases = b.purchase_count || 0;

      if (bPurchases !== aPurchases) {
        return bPurchases - aPurchases;
      }

      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    })
    .slice(0, limit);
}

export function searchProducts(products, query, limit = SEARCH_RESULT_LIMIT) {
  const normalized = query.trim().toLowerCase();

  if (!normalized) {
    return [];
  }

  return filterStorefrontProducts(products, { forSearch: true })
    .filter(product => {
      const name = (product.name || '').toLowerCase();
      const category = (product.category || '').toLowerCase();
      const brand = (product.brand || '').toLowerCase();
      const team = (product.team || '').toLowerCase();
      const brandSlug = (product.brand_slug || '').toLowerCase();

      return (
        name.includes(normalized) ||
        category.includes(normalized) ||
        brand.includes(normalized) ||
        team.includes(normalized) ||
        brandSlug.includes(normalized)
      );
    })
    .slice(0, limit);
}

export async function fetchProductById(productId) {
  const detailSelectWithSports = `
      id,
      name,
      brand,
      brand_slug,
      category,
      sport,
      league,
      team,
      apparel_type,
      gender,
      tags,
      price,
      sale_price,
      color,
      image_url,
      description,
      status,
      hide_product,
      seo_title,
      meta_description,
      product_images (
        id,
        image_url,
        sort_order,
        is_thumbnail,
        variant_id
      ),
      product_sizes (
        id,
        size_label,
        stock_quantity,
        sort_order,
        enabled,
        sku
      ),
      product_variants (
        id,
        name,
        color_hex,
        sort_order
      )
    `;

  const detailSelectLegacy = `
      id,
      name,
      brand,
      category,
      price,
      sale_price,
      color,
      image_url,
      description,
      status,
      hide_product,
      seo_title,
      meta_description,
      product_images (
        id,
        image_url,
        sort_order,
        is_thumbnail,
        variant_id
      ),
      product_sizes (
        id,
        size_label,
        stock_quantity,
        sort_order,
        enabled,
        sku
      ),
      product_variants (
        id,
        name,
        color_hex,
        sort_order
      )
    `;

  let { data, error } = await supabase
    .from('products')
    .select(detailSelectWithSports)
    .eq('id', productId)
    .single();

  if (error && /brand_slug/i.test(error.message || '')) {
    ({ data, error } = await supabase
      .from('products')
      .select(detailSelectWithSports.replace(/^\s*brand_slug,\s*$/m, ''))
      .eq('id', productId)
      .single());
  }

  if (error && /sport|league|team|column/i.test(error.message || '')) {
    ({ data, error } = await supabase
      .from('products')
      .select(detailSelectLegacy)
      .eq('id', productId)
      .single());
  }

  if (error) {
    throw error;
  }

  if (!isProductVisibleOnStorefront(data)) {
    throw new Error('Product is not available.');
  }

  const { sizes, hasSizeVariants, isOutOfStock } = normalizeProductSizes(data);
  const variants = [...(data.product_variants || [])].sort((a, b) => {
    if (Boolean(b.is_default) !== Boolean(a.is_default)) {
      return Number(Boolean(b.is_default)) - Number(Boolean(a.is_default));
    }
    return a.sort_order - b.sort_order;
  });

  return {
    ...data,
    images: normalizeProductImages(data),
    sizes,
    variants,
    hasSizeVariants,
    isOutOfStock,
    defaultVariantId:
      variants.find(variant => variant.is_default)?.id || variants[0]?.id || null,
  };
}

export function getImagesForVariant(images, variantId) {
  if (!variantId) {
    return images;
  }

  const matched = images.filter(image => image.variant_id === variantId);
  return matched.length ? matched : images.filter(image => !image.variant_id);
}
